import { cva, type VariantProps } from 'class-variance-authority';

export const MODAL_VARIANTS = {
  FULLSCREEN: 'fullscreen',
  MEDIUM: 'medium',
  SMALL: 'small',
  DRAWER: 'drawer',
  DEFAULT: 'default',
} as const;

const baseClassName = [
  'group/modal',
  'bg-white',
  'relative',
  'p-0',
  'flex',
  'flex-col',
  'pointer-events-auto',
  'transition-all',
  'duration-300',
  'transform-gpu',
];

const appearTransition = [
  // Shared for open and closed
  'group-data-closed:opacity-0',
  // Entering style
  'group-data-enter:group-data-closed:delay-100',
  'group-data-enter:group-data-closed:translate-y-1/3',
].join(' ');

const slideFromRightTransition = [
  // Shared for open and closed
  'group-data-closed:opacity-0',
  // Entering style
  'group-data-enter:group-data-closed:translate-x-full',
  // Leaving style
  'group-data-leave:group-data-closed:-translate-x-full',
].join(' ');

export const modalVariants = cva(baseClassName, {
  variants: {
    variant: {
      [MODAL_VARIANTS.FULLSCREEN]: [
        'full',
        'bg-white',
        'fixed',
        'rounded-lg',
        'overflow-hidden',
        'h-[calc(100%-var(--modal-padding))]',
        'w-[calc(100vw-var(--sidebar-width)-var(--modal-padding))]',
        'right-4',
        'px-8',
        'py-6',
        'shadow',
        'border',
        'border-stone-200',
        appearTransition,
      ],
      [MODAL_VARIANTS.MEDIUM]: [
        'medium',
        'rounded-lg',
        'w-[50%]',
        'h-[50vh]',
        'max-h-[calc(100%-var(--modal-padding))]',
        'overflow-y-auto',
        'px-8',
        'py-6',
        appearTransition,
      ],
      [MODAL_VARIANTS.SMALL]: [
        'sm',
        'rounded-lg',
        'py-8',
        'w-127',
        'max-h-[calc(100%-var(--modal-padding))]',
        'overflow-y-auto',
        'gap-y-8',
        appearTransition,
      ],
      [MODAL_VARIANTS.DRAWER]: [
        'drawer',
        'gap-y-6',
        'mr-0',
        'h-screen',
        'ml-auto',
        'shadow',
        'py-8',
        'border-l',
        'border-stone-200',
        slideFromRightTransition,
      ],
      [MODAL_VARIANTS.DEFAULT]: [
        'md',
        'rounded-2xl',
        'w-4/6',
        'min-h-120',
        'min-w-200',
        'max-w-304',
        'gap-y-6',
        'max-h-[calc(100%-var(--modal-padding))]',
        'overflow-y-auto',
        appearTransition,
      ],
    },
    shadow: {
      none: 'shadow-none',
      default: [
        '[box-shadow:0px_4px_4px_0px_rgba(0,0,0,0.25)]',
        'border',
        'border-stone-200',
      ],
    },
  },
  defaultVariants: {
    variant: 'default',
  },
});

export type ModalVariants = VariantProps<typeof modalVariants>;
